import {useNavigate, useParams} from "react-router-dom";
import {useCallback, useEffect, useState} from "react";
import {Company} from "../entities/company";
import {useAuth} from "react-oidc-context";
import {isUserTokenManager} from "../util/auth.utils";
import {enqueueSnackbar} from "notistack";
import {CompanyCard} from "../components/CompanyCard";
import {Button, Card, CardHeader, Paper, Typography} from "@mui/material";
import {TokenWithInvitation} from "../entities/token-with-invitation";
import {TokensWithInvitationAndActions} from "../components/TokensWithInvitationAndActions";
import {TokenRequestDialog} from "../components/TokenRequestDialog";
import {NewTokenRequest} from "../entities/new-token-request";
import {TokenRequests} from "../components/TokenRequests";
import {TokenRequest} from "../entities/token-request";

export const MyCompanyPage = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const {companyId} = useParams();
    const [company, setCompany] = useState<Company>()
    const [tokens, setTokens] = useState<TokenWithInvitation[]>([])
    const [tokenRequests, setTokenRequests] = useState<TokenRequest[]>([])
    const [availableTokens, setAvailableTokens] = useState<number>(0)
    const [totalTokens, setTotalTokens] = useState<number>(0)
    const [requestTokenOpen, setRequestTokenOpen] = useState(false)

    const fetchTokenRequests = useCallback(() => {
        return fetch(`/api/companies/${companyId}/token_requests`, {headers: {"Authorization": `Bearer ${auth.user?.access_token}`}})
            .then(res => res.json())
            .then(tokenRequests => setTokenRequests(tokenRequests))
    }, [setTokenRequests, companyId, auth.user?.access_token])

    useEffect(() => {
        if (!companyId || !auth.isAuthenticated || !isUserTokenManager(auth.user)) {
            navigate("/home")
        }
        fetch(`/api/companies/${companyId}`, {headers: {"Authorization": `Bearer ${auth.user?.access_token}`}})
            .then(res => res.json())
            .then((company: Company) => setCompany(company))
            .then(fetchTokenRequests)
            .then(() => fetch(`/api/companies/${companyId}/tokens?status=AVAILABLE`, {headers: {"Authorization": `Bearer ${auth.user?.access_token}`}}))
            .then(res => res.json())
            .then((tokens: TokenWithInvitation[]) => setTokens(tokens))
            .then(() => fetch(`/api/companies/${companyId}/token_stats`, {headers: {"Authorization": `Bearer ${auth.user?.access_token}`}}))
            .then(res => res.json())
            .then((tokenStats) => {
                setAvailableTokens(tokenStats.availableTokens)
                setTotalTokens(tokenStats.totalTokens)
            })
            .catch((err) => {
                console.log(err)
                enqueueSnackbar(`An error has occurred while retrieving information about the company`, {
                    variant: 'error',
                    autoHideDuration: 10_000
                });
                navigate("/my-companies")
            });
    }, [auth, companyId, navigate, fetchTokenRequests]);

    const getPageActions = () => {
        return (
            <>
            {company?.tokenReseller && (<Button onClick={() => {
                    setRequestTokenOpen(true)
                }} disabled={company?.membershipCanceled} variant="outlined">Request more tokens</Button>)}
                <Button onClick={() => fetchAllTokens()} variant="outlined">See all tokens</Button>
            </>)
    }

    const fetchAllTokens = () => {
        fetch(`/api/companies/${companyId}/tokens`, {headers: {"Authorization": `Bearer ${auth.user?.access_token}`}})
            .then(res => res.json())
            .then((tokens: TokenWithInvitation[]) => setTokens(tokens))

    }

    const handleTokenDialogClosed = (newTokenRequest?: NewTokenRequest) => {
        if (newTokenRequest) {
            const formData = new FormData();
            formData.set("amount", `${newTokenRequest.amount}`)
            formData.set("proof", newTokenRequest.proof)
            fetch(`/api/companies/${companyId}/token_requests`, {
                headers: {"Authorization": `Bearer ${auth.user?.access_token}`},
                method: 'POST',
                body:
                formData
            }).then(res => res.json())
                .then(fetchTokenRequests)
                .catch((err) => {
                    console.error(err)
                    enqueueSnackbar("An error has occurred while trying to request tokens", {
                        variant: 'error',
                        autoHideDuration: 5_000
                    })
                })

        }
        setRequestTokenOpen(false)
    }

    return (company ?
        <Paper>
            <CompanyCard company={company} onBackClick={() => navigate("/my-companies")} showAdminDetails={false}/>
            <Card>
                <CardHeader title="Token requests"/>
                <TokenRequests tokenRequests={tokenRequests} onRowClick={() => {
                }}/>
            </Card>
            <Card>
                <CardHeader title={"Tokens"}
                            action={getPageActions()}/>
                <Typography>
                    Tokens: {availableTokens} / {totalTokens}
                </Typography>
                <TokensWithInvitationAndActions tokens={tokens} showAdminInfos={false}
                                                tokenUsed={() => fetchAllTokens()}
                                                tokenRevoked={() => fetchAllTokens()}/>
                {requestTokenOpen && <TokenRequestDialog isOpen={requestTokenOpen} onClose={handleTokenDialogClosed}/>}
            </Card>

        </Paper> : <></>)
}